import { languages } from '@jablotron/lib.spa-lang';
import { addBreadcrumb, captureException } from '@jablotron/lib.spa-sentry';
import i18next, { type i18n as I18n, type InitOptions } from 'i18next';
import HttpBackend from 'i18next-http-backend';
import { type PropsWithChildren, type ReactNode, useEffect, useState } from 'react';
import { I18nextProvider, initReactI18next } from 'react-i18next';
import type { UrlLanguage } from '~/entities/urls/url-language';
import { type LanguageCode, languageCodes } from '~/texts/languages';
import { pickInitialLanguage, SITE_FALLBACK_LANGUAGE } from '~/utils/initialLanguage';

/** A language's UI texts as in public/translations/<lang>.json: term → text. */
export type Translations = Record<string, string>;

export type LanguageProviderProps = PropsWithChildren<{
  /**
   * The language of the address (`router.urlLanguage`). The UI follows it: it starts in its language and
   * switches whenever the address changes language. Without it, or while it names none (the Vite dev server
   * and tests at prefix-less paths), the UI starts in pickInitialLanguage's language.
   */
  urlLanguage?: UrlLanguage;
  /**
   * Texts already at hand, per language. When the start language is among them, i18next is initialised
   * during the first render and the children render at once, without waiting for an effect: what a server
   * render needs (renderToString runs no effects). Other languages are still fetched when switched to.
   */
  resources?: Partial<Record<LanguageCode, Translations>>;
  /** Shown until the start language's texts have loaded, so the UI never flashes its keys. */
  loading?: ReactNode;
}>;

/** Path of the translation files, as lib.spa-lang has it (and the preload in index.html). */
const LOAD_PATH = '/translations/{{lng}}.json';
/** Wait before retrying a failed initialisation, as lib.spa-lang's LanguageWrapper. */
const RETRY_MS = 1000;
const COOKIE_MAX_AGE_S = 60 * 60 * 24 * 365;

/**
 * The language the UI starts in without a language from the address: the path prefix, `?lang=`, the browser,
 * else SITE_FALLBACK_LANGUAGE. On the server (no window) there is nothing to read but the fallback.
 */
function fallbackStartLanguage(): LanguageCode {
  if (typeof window === 'undefined') {
    return SITE_FALLBACK_LANGUAGE;
  }

  return pickInitialLanguage(
    window.location.pathname,
    new URLSearchParams(window.location.search).get('lang'),
    navigator.language,
    Object.keys(languages),
    languageCodes,
    SITE_FALLBACK_LANGUAGE,
  ) as LanguageCode;
}

/**
 * i18next with lib.spa-lang's options (flat keys, no namespace separator, texts from /translations/<lang>.json)
 * plus the site's languages. The start language is also the fallback, so a visitor downloads only their own
 * file (every language file has every key cs.json has; initialLanguage.test.ts). A new instance, not
 * i18next's global one, so renders side by side (tests, a server answering several requests) don't share a
 * language. `initReactI18next` still makes it react-i18next's default (getI18n()) once initialised.
 */
function createI18n(language: LanguageCode, resources: LanguageProviderProps['resources']): I18n {
  const instance = i18next.createInstance({
    compatibilityJSON: 'v4',
    // reloadInterval: i18next-http-backend's default outside a browser is an hourly reload timer per instance, which
    // is never cleared and kept every server render's i18next instance alive (slice 2 spike).
    backend: { loadPath: LOAD_PATH, reloadInterval: false },
    lng: language,
    fallbackLng: language,
    supportedLngs: [...languageCodes],
    nsSeparator: false,
    keySeparator: false,
    ...(resources && {
      resources: Object.fromEntries(
        Object.entries(resources).map(([code, translations]) => [code, { translation: translations }]),
      ),
      // Fetch only the languages not given here; and initialise synchronously, so the first render has texts.
      partialBundledLanguages: true,
      initAsync: false,
    }),
  } satisfies InitOptions);

  return instance.use(initReactI18next).use(HttpBackend);
}

const initialisations = new WeakMap<I18n, Promise<unknown>>();

/** Initialises `i18n` once, however often it's asked (StrictMode runs effects twice); a failure may be retried. */
async function initialise(i18n: I18n): Promise<unknown> {
  let initialisation = initialisations.get(i18n);
  if (!initialisation) {
    initialisation = i18n.init().catch((error: unknown) => {
      initialisations.delete(i18n);
      throw error;
    });
    initialisations.set(i18n, initialisation);
  }

  return initialisation;
}

/**
 * Remembers the address's language for an entry without a language prefix (the Koa server reads it,
 * server/visitorLanguage.mjs): a `/<lang>/` URL always opens its language, the cookie only picks one for `/`.
 */
function rememberLanguage(language: LanguageCode): void {
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `lang=${language}; Path=/; Max-Age=${COOKIE_MAX_AGE_S}; SameSite=Lax${secure}`;
}

/**
 * The app's i18next, in the language of the address (PDR-0019), in place of lib.spa-lang's LanguageWrapper,
 * which picks its language from `?lang`, localStorage and the browser and renders nothing until an effect has
 * run. lib.spa-lang's useTranslate, useLanguage and Translate (and lib.spa-components through them) read
 * i18next from react-i18next's context, which this provides.
 *
 * Opening a `/<lang>/` address, and each change of the address's language, writes the `lang` cookie;
 * `<html lang>` follows the language of the texts shown.
 */
export const LanguageProvider: React.FC<LanguageProviderProps> = ({
  urlLanguage,
  resources,
  loading = null,
  children,
}) => {
  const [i18n] = useState(() => {
    const language = urlLanguage?.current() ?? fallbackStartLanguage();
    const instance = createI18n(language, resources);
    // With the start language's texts at hand this completes synchronously; otherwise the effect below
    // starts it, as fetching the file is no work for a render.
    if (resources?.[language]) {
      void initialise(instance);
    }

    return instance;
  });
  const [ready, setReady] = useState(() => i18n.isInitialized);

  useEffect(() => {
    if (ready) {
      return;
    }
    let cancelled = false;
    let retry: ReturnType<typeof setTimeout> | undefined;
    const attempt = () => {
      initialise(i18n).then(
        () => {
          if (!cancelled) {
            setReady(true);
          }
        },
        (error: unknown) => {
          if (cancelled) {
            return;
          }
          addBreadcrumb({ category: 'language', message: 'i18next initialisation failed, retrying', level: 'error' });
          captureException(error, { level: 'error' });
          retry = setTimeout(attempt, RETRY_MS);
        },
      );
    };
    attempt();

    return () => {
      cancelled = true;
      clearTimeout(retry);
    };
  }, [i18n, ready]);

  useEffect(() => {
    if (!urlLanguage) {
      return;
    }
    const follow = () => {
      const language = urlLanguage.current();
      if (!language) {
        return;
      }
      rememberLanguage(language);
      void initialise(i18n).then(async () => {
        if (i18n.language !== language) {
          await i18n.changeLanguage(language);
        }
      });
    };
    follow();

    return urlLanguage.subscribe(follow);
  }, [i18n, urlLanguage]);

  useEffect(() => {
    if (!ready) {
      return;
    }
    const showLanguage = (language: string) => {
      document.documentElement.lang = language;
    };
    showLanguage(i18n.language);
    i18n.on('languageChanged', showLanguage);

    return () => i18n.off('languageChanged', showLanguage);
  }, [i18n, ready]);

  return ready ? <I18nextProvider i18n={i18n}>{children}</I18nextProvider> : <>{loading}</>;
};
